import React, { useContext, useEffect, useMemo, useState } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { Company, Value, Leadership as LU } from '@ecountabl/lib'
import { textStyles } from '../style'
import { useAppSelector } from '../store'
import { ThemeContext } from '../utility/context/ThemeContext'
import { ValueBadge } from '../utility/value-badge'

type Props = {
  company: Company
  values: Value[]
}

export const Leadership = ({ company, values }: Props): React.ReactElement => {
  const { user } = useAppSelector(state => state.auth)
  const { map } = useAppSelector(state => state.values)
  const [userSelectedImpactAreas, setUserSelectedImpactAreas] = (
    useState<Record<string, boolean>>({})
  )

  const theme = useContext(ThemeContext)
  const { textAlt, textMain, disabled: themeDisabled, primary } = theme.theme.colors

  useEffect(() => {
    if (map) {
      const valuesMap: Record<string, boolean> = {}
      const mapKeys = Object.keys(map)
      const userValuesMap = user.valuedImpactAreaMap
      for (const key of mapKeys) {
        const section = map[key]
        for (const ia of section.impactAreas) {
          if (userValuesMap[ia._id]) {
            valuesMap[ia._id] = true
          }
        }
      }

      setUserSelectedImpactAreas(valuesMap)
    }
  },[map, user])

  const userSelectedValueSlugs = useMemo(() => {
    const slugs = new Set<string>()
    try {
      Object.values(map || {}).forEach((v: any) => {
        const slug = typeof v?.slug === 'string' ? v.slug : undefined
        if (!slug) return
        const ias = Array.isArray(v?.impactAreas) ? v.impactAreas : []
        const anySelected = ias.some((ia: any) => !!userSelectedImpactAreas?.[ia?._id])
        if (anySelected) slugs.add(slug)
      })
    } catch (e) {
      // ignore
    }
    return slugs
  }, [map, userSelectedImpactAreas])

  const alignedValues = useMemo(() => {
    // Prefer the server-provided value alignment list (value metadata).
    const raw = (company as unknown as { valuesAligned?: unknown })?.valuesAligned

    if (Array.isArray(raw)) return raw as unknown[]

    // Fallback: derive from legacy leadership impact areas.
    const badges = LU.leadershipForCompany(company, values)
    return badges.map((ia: any) => {
      const valueSlug = typeof ia?.valueSlug === 'string' ? ia.valueSlug : undefined
      const valueFromProps = valueSlug ? values.find((v) => v.slug === valueSlug) : undefined
      return {
        slug: valueFromProps?.slug || valueSlug,
        label: valueFromProps?.label || ia?.label,
        icon: valueFromProps?.icon,
        color: (valueFromProps as any)?.color,
      }
    })
  }, [company, values])

  const alignedValuesWithMeta = useMemo(() => {
    const valueBySlug = new Map<string, Value>()
    values.forEach((v) => valueBySlug.set(v.slug, v))

    const valueByLabel = new Map<string, Value>()
    values.forEach((v) => valueByLabel.set((v.label || '').toLowerCase(), v))

    return (alignedValues || [])
      .map((v: any) => {
        const slug = typeof v?.slug === 'string' ? v.slug : undefined
        const label = typeof v?.label === 'string' ? v.label : undefined
        const viaSlug = slug ? valueBySlug.get(slug) : undefined
        const viaLabel = label ? valueByLabel.get(label.toLowerCase()) : undefined
        const meta = viaSlug || viaLabel

        const colorRaw = (v as any)?.color ?? (meta as any)?.color
        const color = typeof colorRaw === 'string' ? colorRaw : undefined

        return {
          slug: meta?.slug || slug || '',
          label: meta?.label || label || '',
          icon: (v as any)?.icon || meta?.icon,
          color,
        }
      })
      .filter((v: any) => !!v.slug && !!v.label)
  }, [alignedValues, values])

  const sorted = useMemo(() => {
    const list = [...alignedValuesWithMeta]
    list.sort((a, b) => {
      const aMatch = userSelectedValueSlugs.has(a.slug) ? 1 : 0
      const bMatch = userSelectedValueSlugs.has(b.slug) ? 1 : 0
      if (aMatch !== bMatch) return bMatch - aMatch
      return a.label.localeCompare(b.label)
    })
    return list
  }, [alignedValuesWithMeta, userSelectedValueSlugs])

  return sorted.length ? (
    <View>
      <Text style={[textStyles.h3, {marginBottom: 8, color: textMain}]}>
        Brand Leadership Profile
      </Text>
      <View>
        {sorted.map((v, i) => {
          const matchesUser = userSelectedValueSlugs.has(v.slug)
          const disabledGlyph = themeDisabled || textAlt

          return (
            <View key={v.slug || String(i)} style={styles.item}>
              <ValueBadge
                disabled={!matchesUser}
                color={v.color}
                accentFallbackColor={primary}
                icon={v.icon}
                label={v.label}
                disabledColor={disabledGlyph}
                size={28}
                iconSize={16}
                style={{ marginRight: 10 }}
              />
              <Text style={[textStyles.body, {color: textMain}]}>
                {v.label}
              </Text>
              {!matchesUser ? (
                <Text style={[styles.notUserTag, { color: textAlt }]}>
                  {/* spacer / subtle indicator; keep empty if you don’t want text */}
                </Text>
              ) : null}
            </View>
          )
        })}
      </View>
    </View>
  ) : null
}

export default Leadership

const styles = StyleSheet.create({
  item: {
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center'
  },
  notUserTag: {
    marginLeft: 8,
  }
})